<template>
  <v-container style="max-width: 100%">
    <v-card
      flat
      class="d-flex align-center justify-center py-4 mb-4"
    >
      <v-btn
        icon
        @click="prevWeek"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn
        small
        class="mr-2"
        :disabled="currentWeekOffset === 0"
        @click="goToday"
      >
        This week
      </v-btn>

      <strong 
        :class="{ 'green--text': currentWeekOffset === 0 }"
        style="font-size: 16px;"
      >
        {{ weekRange.label }}
      </strong>

      <v-btn
        icon
        @click="nextWeek"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-card>

    <v-tabs
      v-model="tab"
      grow
    >
      <v-tab
        v-for="(itemTab, index) in itemsTab"
        :key="itemTab"
      >
        <span class="ppc_darkgreen--text">
          {{ itemTab }}

          <!-- clients -->
          <v-chip 
            v-if="index === 0"
            small 
            class="ml-2" 
            color="grey lighten-2"
          >
            {{ clientsTable.length }}
          </v-chip>

          <!-- pickups -->
          <v-chip 
            v-if="index === 1"
            small 
            class="ml-2" 
            color="grey lighten-2"
          >
            {{ pickupCount }}
          </v-chip>

          <!-- drop-offs -->
          <v-chip 
            v-if="index === 2"
            small 
            class="ml-2" 
            color="grey lighten-2"
          >
            {{ projectDropoffs.filter(d => !d.dropoffPerson).length }}/{{ projectDropoffs.length }}
          </v-chip>
        </span>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card
          flat
          class="px-4 pb-2 my-4 d-flex flex-wrap align-center"
          style="gap: 16px;"
        >
          <div 
            class="legend-item"
            :class="{ 'legend-active': activeTypes.includes('project') }"
            @click="toggleType('project')"
          >
            <span class="legend-box range-project" /> Project
          </div>

          <div 
            class="legend-item"
            :class="{ 'legend-active': activeTypes.includes('accommodation') }"
            @click="toggleType('accommodation')"
          >
            <span class="legend-box range-accommodation" /> Accommodation
          </div>

          <div 
            class="legend-item"
            :class="{ 'legend-active': activeTypes.includes('spanish') }"
            @click="toggleType('spanish')"
          >
            <span class="legend-box range-spanish" /> Spanish
          </div>

          <div 
            class="legend-item"
            :class="{ 'legend-active': activeTypes.includes('tour') }"
            @click="toggleType('tour')"
          >
            <span class="legend-box range-tour" /> Tours
          </div>

          <div class="legend-item">
            <span class="legend-box range-overlap" /> Overlap
          </div>

          <div class="legend-item">
            <span class="legend-box active-today-example" /> Active today
          </div>

          <div class="legend-item">
            <v-icon
              small
              color="green"
              class="mr-1"
            >
              mdi-circle
            </v-icon> Executing / All set
          </div>

          <div class="legend-item">
            <v-icon
              small
              color="orange"
              class="mr-1"
            >
              mdi-circle
            </v-icon> Pay provider
          </div>

          <div class="legend-item">
            <v-icon
              small
              color="red"
              class="mr-1"
            >
              mdi-circle
            </v-icon> Problem / Other
          </div>
        </v-card>

        <v-data-table
          :headers="headersClients"
          :items="clientsTable"
          item-key="id"
          sort-by="start"
          class="elevation-1"
          :search="searchClients"
          :items-per-page="-1"
          disable-pagination
          hide-default-footer
        >
          <template v-slot:[`item.full_name`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex flex-row align-center">
                  <v-btn 
                    x-small
                    fab
                    v-bind="attrs"
                    class="mr-3" 
                    v-on="on"
                    @click="goClient(item.id)"
                  >
                    <v-icon>mdi-arrow-up-bold</v-icon>
                  </v-btn>
                  <strong>{{ item.full_name }}</strong>
                </div>
              </template>
              <span>Go to Client</span>
            </v-tooltip>
          </template>
            
          <template v-slot:[`item.gender`]="{ item }">
            <v-icon
              v-if="`${item.gender}` === 'Male'"
              small
              color="ppc_darkgreen"
            >
              fa-male
            </v-icon>
            <v-icon
              v-else-if="`${item.gender}` === 'Female'"
              small
              color="ppc_darkgreen"
            >
              fa-female
            </v-icon>
            <v-icon
              v-else
              small
              color="ppc_darkgreen"
            >
              fa-genderless
            </v-icon>
          </template>

          <template v-slot:[`item.project`]="{ item }">
            <div v-if="activeTypes.includes('project') && item.project && item.project.length">
              <div
                v-for="(project, index) in item.project"
                :key="index"
                :class="[
                  'mb-2',
                  'range-block',
                  'range-project',
                  isOverlapping(item.projectStart, item.projectEnd, index) ? 'range-overlap' : '',
                  isActiveToday(item.projectStart[index], item.projectEnd[index]) ? 'active-today-border' : ''
                ]"
              >
                <div 
                  style="cursor: pointer;"
                  @click="goProject(item.projectId[index])"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        :color="getStatusDotColor(item.projectStatus[index])"
                        v-on="on"
                      >
                        mdi-circle
                      </v-icon>
                    </template>
                    <span>{{ getStatusName(item.projectStatus[index]) }}</span>
                  </v-tooltip>
                  {{ project }}
                </div>
                <small
                  v-if="item.projectStart[index] && item.projectEnd[index]"
                  class="grey--text"
                >
                  {{ formattedDate(item.projectStart[index], 'monthAndDay') }} →
                  {{ formattedDate(item.projectEnd[index], 'monthAndDay') }}
                </small>
              </div>
            </div>
          </template>

          <template v-slot:[`item.accommodation`]="{ item }">
            <div v-if="activeTypes.includes('accommodation') && item.accommodation && item.accommodation.length">
              <div
                v-for="(acc, index) in item.accommodation"
                :key="'wrapper-' + item.id + '-' + index"
              >
                <!-- 🔴 GAP BETWEEN BLOCKS -->
                <div
                  v-if="index > 0 && item.accommodationGaps?.includes(index)"
                  :key="'gap-' + item.id + '-' + index"
                  class="gap-line"
                >
                  <strong>⚠ Gap between stays</strong>
                </div>

                <!-- ✅ ACTUAL BLOCK -->
                <div
                  :class="[
                    'mb-2',
                    'range-block',
                    'range-accommodation',
                    isOverlapping(item.accommodationStart, item.accommodationEnd, index, true) ? 'range-overlap' : '',
                    isActiveToday(item.accommodationStart[index], item.accommodationEnd[index]) ? 'active-today-border' : ''
                  ]"
                >
                  <div
                    style="cursor: pointer;"
                    @click="goAccommodation(item.accommodationId[index])"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          small
                          v-bind="attrs"
                          :color="getStatusDotColor(item.accommodationStatus[index])"
                          v-on="on"
                        >
                          mdi-circle
                        </v-icon>
                      </template>
                      <span>{{ getStatusName(item.accommodationStatus[index]) }}</span>
                    </v-tooltip>

                    {{ acc }}
                  </div>

                  <small class="grey--text">
                    {{ formattedDate(item.accommodationStart[index], 'monthAndDay') }} →
                    {{ formattedDate(item.accommodationEnd[index], 'monthAndDay') }}
                  </small>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:[`item.spanish`]="{ item }">
            <div v-if="activeTypes.includes('spanish') && item.spanish && item.spanish.length">
              <div
                v-for="(spanish, index) in item.spanish"
                :key="index"
                :class="[
                  'mb-2',
                  'range-block',
                  'range-spanish',
                  isOverlapping(item.spanishStart, item.spanishEnd, index) ? 'range-overlap' : '',
                  isActiveToday(item.spanishStart[index], item.spanishEnd[index]) ? 'active-today-border' : ''
                ]"
              >
                <div 
                  style="cursor: pointer;"
                  @click="goTeaching(item.spanishId[index])"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        :color="getStatusDotColor(item.spanishStatus[index])"
                        v-on="on"
                      >
                        mdi-circle
                      </v-icon>
                    </template>
                    <span>{{ getStatusName(item.spanishStatus[index]) }}</span>
                  </v-tooltip>
                  <v-icon
                    v-if="item.spanishLocation && item.spanishLocation[index] === 'Online'"
                    small
                    class="mr-1"
                  >
                    {{ '$vuetify.icons.icon_mdi_headphones' }}
                  </v-icon>
                  {{ spanish }}
                </div>
                <small
                  v-if="item.spanishStart[index] && item.spanishEnd[index]"
                  class="grey--text"
                >
                  {{ formattedDate(item.spanishStart[index], 'monthAndDay') }} →
                  {{ formattedDate(item.spanishEnd[index], 'monthAndDay') }}
                </small>
              </div>
            </div>
          </template>

          <template v-slot:[`item.tour`]="{ item }">
            <div v-if="activeTypes.includes('tour') && item.tour && item.tour.length">
              <div
                v-for="(tour, index) in item.tour"
                :key="index"
                :class="[
                  'mb-2',
                  'range-block',
                  'range-tour',
                  isOverlapping(item.tourStart, item.tourEnd, index) ? 'range-overlap' : '',
                  isActiveToday(item.tourStart[index], item.tourEnd[index]) ? 'active-today-border' : ''
                ]"
              >
                <div 
                  style="cursor: pointer;"
                  @click="goTour(item.tourId[index])"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        :color="getStatusDotColor(item.tourStatus[index])"
                        v-on="on"
                      >
                        mdi-circle
                      </v-icon>
                    </template>
                    <span>{{ getStatusName(item.tourStatus[index]) }}</span>
                  </v-tooltip>
                  {{ tour }}
                </div>
                <small
                  v-if="item.tourStart[index] && item.tourEnd[index]"
                  class="grey--text"
                >
                  {{ formattedDate(item.tourStart[index], 'monthAndDay') }} →
                  {{ formattedDate(item.tourEnd[index], 'monthAndDay') }}
                </small>
              </div>
            </div>
          </template>

          <template v-slot:[`item.programProgress`]="{ item }">
            <div
              v-if="item.programProgress"
              class="d-flex align-center"
            >
              <!-- main day counter -->
              <v-chip
                small
                class="mr-2"
              >
                {{ item.programProgress.replace(' ⚠️','').replace(' 🔚','') }}
              </v-chip>

              <!-- first week -->
              <v-chip
                v-if="item.programProgress.includes('⚠️')"
                small
                color="yellow"
                text-color="black"
              >
                First
              </v-chip>

              <!-- last week -->
              <v-chip
                v-else-if="item.programProgress.includes('🔚')"
                small
                color="red"
                text-color="white"
              >
                Last
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.arrived`]="{ item }">
            <span v-if="item.arrived">
              {{ item.arrived }}

              <v-tooltip
                v-if="item.pickupWarning"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon 
                    small 
                    color="red"
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-alert
                  </v-icon>
                </template>
                <span>Pickup date differs from accommodation start</span>
              </v-tooltip>

            </span>
          </template>

          <template
            slot="item.comment"
            slot-scope="{ item }"
          >
            <div class="d-flex align-center">
              <v-text-field
                v-model="item.comment"
                dense
                hide-details
                placeholder="Weekly note..."
                @change="saveWeeklyComment(item)"
              />

              <v-btn
                icon
                small
                @click="showCommentHistory(item)"
              >
                <v-icon small>
                  mdi-history
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-tab-item>

      <v-tab-item>
        <v-btn
          small
          class="ma-4"
          @click="printPickupList"
        >
          🖨 Print Pickup List
        </v-btn>

        <v-data-table
          :headers="headersPickup"
          :items="pickupClients"
          item-key="resourceId"
          class="elevation-1"
          :items-per-page="-1"
          disable-pagination
          hide-default-footer
        >
          <!-- Name -->
          <template v-slot:[`item.full_name`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex flex-row align-center">
                  <v-btn 
                    x-small
                    fab
                    v-bind="attrs"
                    class="mr-2" 
                    v-on="on"
                    @click="goClient(item.id)"
                  >
                    <v-icon small>
                      mdi-arrow-up-bold
                    </v-icon>
                  </v-btn>
                  <strong>{{ item.full_name }}</strong>
                </div>
              </template>
              <span>Open Client</span>
            </v-tooltip>
          </template>

          <!-- Arrived + warning -->
          <template v-slot:[`item.arrived`]="{ item }">
            <span>
              {{ item.arrived }}

              <v-tooltip
                v-if="item.pickupWarning"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon 
                    small 
                    color="red"
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-alert
                  </v-icon>
                </template>
                <span>Pickup date differs from accommodation start</span>
              </v-tooltip>
            </span>
          </template>

          <!-- First accommodation -->
          <template v-slot:[`item.accommodation`]="{ item }">
            <div>
              <!-- FIRST accommodation (truth) -->
              <div class="range-block range-accommodation mb-1">
                <div 
                  style="cursor: pointer;"
                  @click="goAccommodation(item.resourceId)"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        :color="getStatusDotColor(item.accommodationStatus)"
                        class="mr-1"
                        v-on="on"
                      >
                        mdi-circle
                      </v-icon>
                    </template>
                    <span>{{ getStatusName(item.accommodationStatus) }}</span>
                  </v-tooltip>

                  <strong>
                    {{ item.resourceName }}
                  </strong>
                </div>

                <small class="grey--text">
                  {{ item.start 
                    ? formattedDate(item.start, 'monthAndDay') 
                    : '—' }}
                </small>

                <small class="grey--text">
                  {{ item.end }}
                </small>

                <!-- 📞 Family phone -->
                <div 
                  v-if="
                    item.family === 1 &&
                      getContactInfo(item.contact_id)
                  " 
                  class="pickup-line"
                >
                  <span v-if="getContactInfo(item.contact_id)">
                    {{ getContactInfo(item.contact_id).name }}
                    <a 
                      v-if="getContactInfo(item.contact_id).phone" 
                      :href="`tel:${getContactInfo(item.contact_id).phone}`"
                    >
                      📞 {{ getContactInfo(item.contact_id).phone }}
                    </a>
                  </span>
                </div>
              </div>

            </div>
          </template>

          <!-- Staff picker with warning tooltip for non-family accommodation -->
          <template v-slot:[`item.pickupStaff`]="{ item }">
            <v-tooltip
              v-if="item.family !== 1"
              top
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-select
                    v-model="item.pickupStaff"
                    :items="$store.state.users"
                    item-text="name"
                    item-value="id"
                    clearable
                    clear-icon="mdi-close-circle"
                    hide-details
                    placeholder="Assign..."
                    :menu-props="{ contentClass: 'pickup-menu' }"
                    :class="{
                      'red lighten-4': item.family === 0 && !item.pickupStaff,
                      'green lighten-4': item.family === 0 && item.pickupStaff
                    }"
                    @change="savePickupStaff(item)"
                    @click:clear="clearPickupStaff(item)"
                  >
                    <template v-slot:item="{ item }">
                      <div class="pickup-item">
                        👤 {{ item.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </template>
              <span>
                {{ item.pickupStaff 
                  ? '✓ Pickup assigned (non-family stay)' 
                  : '⚠ Non-family accommodation' }}
              </span>
            </v-tooltip>

            <v-select
              v-else
              v-model="item.pickupStaff"
              :items="$store.state.users"
              item-text="name"
              item-value="id"
              clearable
              dense
              hide-details
              placeholder="Assign..."
              :menu-props="{ contentClass: 'pickup-menu' }"
              @change="savePickupStaff(item)"
              @click:clear="clearPickupStaff(item)"
            />
          </template>

          <!-- Source -->
          <template v-slot:[`item.source`]="{ item }">
            <span>{{ item.source }}</span>
          </template>

          <!-- Pickup details -->
          <template v-slot:[`item.pickupDetails`]="{ item }">
            <div
              v-if="
                item.pickup_type ||
                  item.pickup_company ||
                  item.pickup_nr ||
                  item.pickup_from ||
                  item.pickup_time ||
                  item.pickup_address
              "
              class="pickup-group"
            >
              <!-- Header -->
              <div class="pickup-header">
                <span class="mr-1">
                  {{ getPickupIcon(item.pickup_type) }}
                </span>
                {{ item.pickup_type || 'Pickup' }}
                <span v-if="item.pickup_time">• {{ item.pickup_time }}</span>
              </div>

              <!-- Details -->
              <div
                v-if="item.pickup_company || item.pickup_nr"
                class="pickup-line"
              >
                {{ item.pickup_company || '' }}
                <span v-if="item.pickup_nr">#{{ item.pickup_nr }}</span>
              </div>

              <div
                v-if="item.pickup_from"
                class="pickup-line"
              >
                ⬅️ From: {{ item.pickup_from }}
              </div>

              <div
                v-if="item.pickup_address"
                class="pickup-line grey--text"
              >
                {{ item.pickup_address }}
              </div>
            </div>
          </template>
        </v-data-table>

        <div
          id="printPickup"
          style="display:none"
        >
          <h2>Pickup List – {{ weekRange.label }}</h2>

          <table class="print-table compact">
            <tr>
              <th>Name</th>
              <th>Source</th>
              <th>Arrival</th>
              <th>Until</th>
              <th>Accommodation</th>
              <th>Pickup</th>
              <th>Staff</th>
            </tr>

            <tr
              v-for="client in pickupClients"
              :key="client.id"
            >
              <td>{{ client.full_name }}</td>
              <td>{{ client.source }}</td>
              <td>{{ client.arrived }}</td>
              <td>{{ client.end }}</td>
              <td>
                {{ client.firstAccommodationName }}<br>
                <small>
                  {{ client.firstAccommodationStart 
                    ? formattedDate(client.firstAccommodationStart, 'monthAndDay') 
                    : '' }}
                </small>

                <!-- 📞 Family contact -->
                <div
                  v-if="
                    client.firstAccommodationFamily === 1 &&
                      getContactInfo(client.firstAccommodationContactId)
                  "
                >
                  <small>
                    {{ getContactInfo(client.firstAccommodationContactId).name }}
                    <span v-if="getContactInfo(client.firstAccommodationContactId).phone">
                      ({{ getContactInfo(client.firstAccommodationContactId).phone }})
                    </span>
                  </small>
                </div>
              </td>
              <td>
                <div
                  v-if="
                    client.pickup_type ||
                      client.pickup_company ||
                      client.pickup_nr ||
                      client.pickup_from ||
                      client.pickup_time
                  "
                >
                  <strong>
                    {{ client.pickup_type || 'Pickup' }}
                    {{ client.pickup_time ? '• ' + client.pickup_time : '' }}
                  </strong>
                  <br>
                  <small>
                    {{ client.pickup_company || '' }}
                    <span v-if="client.pickup_nr">#{{ client.pickup_nr }}</span>
                    <br v-if="client.pickup_from">
                    From: {{ client.pickup_from || '' }}
                  </small>
                </div>
              </td>
              <td>{{ getUserName(client.pickupStaff) || '—' }}</td>
            </tr>
          </table>
        </div>
      </v-tab-item>

      <v-tab-item>
        <v-btn
          small
          class="ma-4"
          @click="printDropoffList"
        >
          🖨 Print Drop-off List
        </v-btn>

        <v-data-table
          :headers="headersProjectDropoffs"
          :items="projectDropoffs"
          class="elevation-1"
          :items-per-page="-1"
          disable-pagination
          hide-default-footer
        >
          <!-- Client -->
          <template v-slot:[`item.full_name`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex flex-row align-center">
                  <v-btn 
                    x-small
                    fab
                    v-bind="attrs"
                    class="mr-2" 
                    v-on="on"
                    @click="goClient(item.client_id)"
                  >
                    <v-icon small>
                      mdi-arrow-up-bold
                    </v-icon>
                  </v-btn>
                  <strong>{{ item.full_name }}</strong>
                </div>
              </template>
              <span>Open Client</span>
            </v-tooltip>
          </template>

          <!-- Project -->
          <template v-slot:[`item.resourceName`]="{ item }">
            <div class="range-block range-project">
              <div 
                style="cursor: pointer;"
                @click="goProject(item.resourceId)"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      class="mr-1"
                      v-bind="attrs"
                      :color="getDropoffStatusColor(item.statusResourceId)"
                      v-on="on"
                    >
                      mdi-circle
                    </v-icon>
                  </template>
                  <span>
                    {{ getResourceStatus(item.statusResourceId)?.name || 'Unknown status' }}
                  </span>
                </v-tooltip>

                <strong>{{ item.resourceName }}</strong>
              </div>

              <small class="grey--text">
                {{ formattedDate(item.start, 'monthAndDay') }} →
                {{ formattedDate(item.end, 'monthAndDay') }}
              </small>
            </div>
          </template>

          <!-- Start -->
          <template v-slot:[`item.start`]="{ item }">
            {{ formattedDate(item.start, 'monthAndDay') }}
          </template>

          <!-- End -->
          <template v-slot:[`item.end`]="{ item }">
            {{ formattedDate(item.end, 'monthAndDay') }}
          </template>

          <template v-slot:[`item.dropoffPerson`]="{ item }">
            <v-combobox
              v-model="item.dropoffPerson"
              :items="$store.state.users"
              item-text="name"
              item-value="name"
              clearable
              dense
              hide-details
              placeholder="Assign..."
              :class="{
                'red lighten-4': !item.dropoffPerson,
                'green lighten-4': item.dropoffPerson
              }"
              @change="saveDropoffStaff(item)"
            />
          </template>

          <template v-slot:[`item.dropoff_time`]="{ item }">
            <v-text-field
              v-model="item.dropoff_time"
              type="time"
              dense
              hide-details
              @change="saveDropoffDetails(item)"
            />
          </template>

          <template v-slot:[`item.dropoff_from`]="{ item }">
            <v-text-field
              v-model="item.dropoff_from"
              dense
              hide-details
              placeholder="Pickup location..."
              @change="saveDropoffDetails(item)"
            />
          </template>

          <template v-slot:[`item.dropoff_comment`]="{ item }">
            <v-text-field
              v-model="item.dropoff_comment"
              dense
              hide-details
              placeholder="Comments..."
              @change="saveDropoffDetails(item)"
            />
          </template>

        </v-data-table>

        <div
          id="printDropoff"
          style="display:none"
        >
          <h2>Drop-off List – {{ weekRange.label }}</h2>

          <table class="print-table compact">
            <tr>
              <th>Name</th>
              <th>Project</th>
              <th>Dates</th>
              <th>Status</th>
              <th>Time</th>
              <th>From</th>
              <th>Staff</th>
              <th>Comments</th>
            </tr>

            <tr 
              v-for="item in projectDropoffs.slice().sort((a,b) => new Date(a.start) - new Date(b.start))" 
              :key="item.resourceId"
            >
              <td>{{ item.full_name }}</td>

              <td>
                <strong>{{ item.resourceName }}</strong>
              </td>

              <td>
                {{ formattedDate(item.start, 'monthAndDay') }} →
                {{ formattedDate(item.end, 'monthAndDay') }}
              </td>

              <td>
                {{ getResourceStatus(item.statusResourceId)?.name || '—' }}
              </td>

              <td>{{ item.dropoff_time || '—' }}</td>

              <td>{{ item.dropoff_from || '—' }}</td>

              <td>
                {{ getUserName(item.dropoffPerson) || '—' }}
              </td>

              <td>{{ item.dropoff_comment || '—' }}</td>

            </tr>
          </table>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog
      v-model="commentHistoryDialog"
      max-width="700"
    >
      <v-card>
        <v-card-title>
          Weekly Comments
          <v-spacer />

          <v-btn
            small
            outlined
            class="mr-2"
            @click="copyCommentHistory"
          >
            <v-icon left small>
              mdi-content-copy
            </v-icon>
            Copy
          </v-btn>

          <span class="grey--text text--darken-1">
            {{ commentHistoryClient }}
          </span>
        </v-card-title>

        <v-card-text>
          <v-timeline dense>
            <v-timeline-item
              v-for="(entry, index) in commentHistory"
              :key="index"
              small
            >
              <div>
                <strong>
                  {{ formattedDate(entry.week_start, 'monthAndDay') }}
                </strong>

                <div class="grey--text text--darken-1 text-caption">
                  Updated:
                  {{ formattedDate(entry.updated_at, 'dateTime') }}
                </div>

                <div class="mt-2">
                  {{ entry.comment }}
                </div>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="commentHistoryDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

  
</template>

<script>
import inputRules from '../mixins/inputRules';
import formattedDate from '../mixins/formattedDate';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default {

  mixins: [inputRules, formattedDate],

  data() {
    return {
      arrivals: null,
      dialogAddLead: false,
      loading: false,
      itemsNationality: [],
      itemsSource: [],
      errorMessageNames: '',
      first_name: '',
      last_name: '',
      sourceId: 1,
      agencyId: null,
      clientsTable: [],
      headersClientsBase: [
        { text: 'Full Name', value: 'full_name', width: "100px" },
        { text: 'Source/Agency', value: 'source', width: "100px" },
        // { text: 'Arrived', value: 'arrived', width: "50px" },
        { text: 'Days', value: 'programProgress', width: "50px" },
        { text: 'Project', value: 'project', width: "220px" },
        { text: 'Accommodation', value: 'accommodation', width: "220px" },
        { text: 'Spanish', value: 'spanish', width: "220px" },
        { text: 'Tours', value: 'tour', width: "220px" },
        { text: 'Comments', value: 'comment', width: "220px" },
      ],
      headersPickup: [
        { text: 'Full Name', value: 'full_name', width: "120px" },
        { text: 'Source/Agency', value: 'source', width: "140px" },
        { text: 'Arrival (MM-DD)', value: 'arrived', width: "80px" },
        { text: 'Accommodation', value: 'accommodation', width: "200px" },
        { text: 'Pickup Details', value: 'pickupDetails', width: "260px" },
        { text: 'Pickup Staff', value: 'pickupStaff', width: "160px" },
      ],

      headersProjectDropoffs: [
        { text: 'Full Name', value: 'full_name', width: "140px" },
        { text: 'Project', value: 'resourceName', width: "260px" },
        { text: 'First day (MM-DD)', value: 'start', width: "100px" },
        { text: 'Drop-off Time', value: 'dropoff_time', width: "100px" },
        { text: 'Picking Up From', value: 'dropoff_from', width: "260px" },
        { text: 'Drop-off Staff', value: 'dropoffPerson', width: "160px" },
        { text: 'Comments', value: 'dropoff_comment', width: "220px" },
      ],

      projectDropoffs: [],
      addLeadValid: true,
      arrayUpload: [],
      searchClients: '',
      itemsTab: [
        'Weekly Client Activities', 'Pickup Week List', 'Project Drop-off Week List'
      ],
      pickupClients: [],
      commentHistoryDialog: false,
      commentHistory: [],
      commentHistoryClient: null,
    }
  },
  computed: {
    successMessage () {
      return this.$store.getters.successMessage
    },

    headersClients() {
      return this.headersClientsBase.filter(header => {
        if (header.value === 'project') return this.activeTypes.includes('project');
        if (header.value === 'accommodation') return this.activeTypes.includes('accommodation');
        if (header.value === 'spanish') return this.activeTypes.includes('spanish');
        if (header.value === 'tour') return this.activeTypes.includes('tour');
        return true; // always keep name + source
      });
    },

    weekRange() {
      const base = dayjs().add(this.currentWeekOffset, 'week');

      const start = base.startOf('week').add(1, 'day');   // Monday
      const end = base.endOf('week').add(1, 'day');       // Sunday

      return {
        start,
        end,
        label: `Week ${start.week()} (${start.format('MMM DD')} – ${end.format('MMM DD')})`
      };
    },

    contactsById() {
      return this.$store.getters.contactsById;
    },

    pickupCount() {
      return this.pickupClients.length;
    },

    activeTypes: {
      get() {
        return this.$store.state.activeDashboardTypes || [
          'project', 'accommodation', 'spanish', 'tour'
        ];
      },
      set(val) {
        this.$store.commit('SET_ACTIVE_DASHBOARD_TYPES', val);
      }
    },

    dashboardSettings() {
      return this.$store.getters.dashboardSettings;
    },

    tab: {
      get() {
        return this.dashboardSettings.tab;
      },
      set(val) {
        this.$store.dispatch('setDashboardSettings', { tab: val });
      }
    },

    currentWeekOffset: {
      get() {
        return this.dashboardSettings.weekOffset;
      },
      set(val) {
        this.$store.dispatch('setDashboardSettings', { weekOffset: val });
      }
    },

  },
  
  watch: {
    successMessage: function () {
      if (this.successMessage !== "") {
        this.alertSuccessMessage = true;
      }
    },
  },

  beforeCreate () {
    this.$store.dispatch('setLoadingApp', 1)
  },

  created () {
    dayjs.locale("en");
    this.initialize();
  },

  methods: {

    isToday(start, end) {
      if (!start || !end) return false;

      const today = dayjs();
      const s = dayjs(start);
      const e = dayjs(end).endOf('day');

      return (
        (today.isAfter(s) && today.isBefore(e)) ||
        today.isSame(s, 'day') ||
        today.isSame(e, 'day')
      );
    },

    isOverlapping(starts, ends, index, isAccommodation = false) {
      const currentStart = new Date(starts[index]);
      const currentEnd = new Date(ends[index]);

      for (let i = 0; i < starts.length; i++) {
        if (i === index) continue;

        const otherStart = new Date(starts[i]);
        const otherEnd = new Date(ends[i]);

        if (isAccommodation) {
          // 🔹 touching dates = NOT overlap
          if (currentStart < otherEnd && currentEnd > otherStart) {
            return true;
          }
        } else {
          // 🔹 original behavior
          if (currentStart <= otherEnd && currentEnd >= otherStart) {
            return true;
          }
        }
      }

      return false;
    },

    async initialize () {
      this.$store.dispatch('breadcrumbs', ['DashboardOps']);
      await this.getClients();
      await this.getPickups();
      await this.getProjectDropoffs();
    },

    goClient(id) {
      this.$store.dispatch('setLoadingApp', 1);
      this.$router.push({ name: 'Client', params: { id: id } })
    },
    
    getStatus(id) {
      return this.$store.state.statusClients.filter((select_item) => {
        return select_item.id === id;
      })[0];
    },

    async getClients() {
      this.$store.dispatch('setLoadingApp', 1);

      const results = await this.$store.dispatch('getDataDashboardOps', {
        start: this.weekRange.start.format('YYYY-MM-DD'),
        end: this.weekRange.end.format('YYYY-MM-DD')
      });

      // console.log("getDataDashboardOps:", results.length);

      const startOfWeek = this.weekRange.start;
      const endOfWeek = this.weekRange.end;

      const filtered = results.filter(client => {
        
        const start = client.start ? dayjs(client.start) : null;
        const end = client.end ? dayjs(client.end) : null;

        if (!start || !end) return false;

        return (
          start.isBefore(endOfWeek) || start.isSame(endOfWeek)
        ) && (
          end.isAfter(startOfWeek) || end.isSame(startOfWeek)
        );
      });

      const grouped = {};

      for (const row of filtered) {     

        const clientId = row.id;

        if (!grouped[clientId]) {
          grouped[clientId] = {
            ...row,
            project: [],
            projectStart: [],
            projectEnd: [],
            projectId: [],
            projectStatus: [],
            spanish: [],
            spanishStart: [],
            spanishEnd: [],
            spanishId: [],
            spanishStatus: [],
            spanishLocation: [],
            accommodation: [],
            accommodationStart: [],
            accommodationEnd: [],
            accommodationId: [],
            accommodationStatus: [],
            tour: [],
            tourStart: [],
            tourEnd: [],
            tourId: [],
            tourStatus: [],
          };
        }

        switch (row.type_id) {
          case 1:
          case 2:
            grouped[clientId].project.push(row.resourceName);
            grouped[clientId].projectStart.push(row.start);
            grouped[clientId].projectEnd.push(row.end);
            grouped[clientId].projectId.push(row.resourceId);
            grouped[clientId].projectStatus.push(row.statusResourceId);
            break;

          case 3:
            grouped[clientId].spanish.push(row.resourceName);
            grouped[clientId].spanishStart.push(row.start);
            grouped[clientId].spanishEnd.push(row.end);
            grouped[clientId].spanishId.push(row.resourceId);
            grouped[clientId].spanishStatus.push(row.statusResourceId);
            grouped[clientId].spanishLocation.push(row.class_location);
            break;

          case 4:
            grouped[clientId].accommodation.push(row.resourceName);
            grouped[clientId].accommodationStart.push(row.start);
            grouped[clientId].accommodationEnd.push(row.end);
            grouped[clientId].accommodationId.push(row.resourceId);
            grouped[clientId].accommodationStatus.push(row.statusResourceId);
            break;

          case 5:
            grouped[clientId].tour.push(row.resourceName);
            grouped[clientId].tourStart.push(row.start);
            grouped[clientId].tourEnd.push(row.end);
            grouped[clientId].tourId.push(row.resourceId);
            grouped[clientId].tourStatus.push(row.statusResourceId);
            break;
        }
      }

      // ---- Compute programProgress per client (correct place) ----
      for (const clientId in grouped) {
        const client = grouped[clientId];

        let programProgress = '';

        // ✅ Prefer accommodation (best for ops)
        let startDate = null;
        let endDate = null;

        if (client.accommodationStart?.length) {
          const ranges = client.accommodationStart.map((start, i) => ({
            start: dayjs(start),
            end: dayjs(client.accommodationEnd[i]),
            originalIndex: i
          }));

          // sort by start date
          ranges.sort((a, b) => a.start.valueOf() - b.start.valueOf());

          // full span
          startDate = ranges[0].start;
          endDate = ranges[ranges.length - 1].end;

          const gapIndexes = [];

          for (let i = 1; i < ranges.length; i++) {
            const prev = ranges[i - 1];
            const curr = ranges[i];

            const prevEnd = prev.end.startOf('day');
            const currStart = curr.start.startOf('day');

            const isSameDay = currStart.isSame(prevEnd, 'day');
            const isOverlap = currStart.isBefore(prevEnd);

            // GAP ONLY → strictly after AND not same day AND not overlap
            if (!isSameDay && !isOverlap && currStart.isAfter(prevEnd)) {
              gapIndexes.push(i);
            }
          }

          client.accommodationGaps = [...new Set(gapIndexes)];

        } else if (client.start && client.end) {
          startDate = client.start;
          endDate = client.end;
        }

        if (startDate && endDate) {
          const start = dayjs(startDate);
          const end = dayjs(endDate);

          const ref = this.weekRange.start.add(3, 'day');

          const totalDays = end.diff(start, 'day') + 1;
          const currentDay = Math.min(
            totalDays,
            Math.max(1, ref.diff(start, 'day') + 1)
          );

          programProgress = `${currentDay}/${totalDays}`;

          if (currentDay <= 7) {
            programProgress += ' ⚠️';
          } else if (currentDay > totalDays - 7) {
            programProgress += ' 🔚';
          }

        }

        client.programProgress = programProgress;
      }


      const merged = Object.values(grouped).map(client => ({
        ...client,
        project: client.project,
        projectStart: client.projectStart,
        projectEnd: client.projectEnd,
        spanish: client.spanish,
        spanishStart: client.spanishStart,
        spanishEnd: client.spanishEnd,
        accommodation: client.accommodation,
        accommodationStart: client.accommodationStart,
        accommodationEnd: client.accommodationEnd,
        tour: client.tour,
        tourStart: client.tourStart,
        tourEnd: client.tourEnd,
      }));

      this.clientsTable = merged.map(client => {
        const nationality = this.$store.state.itemsNationality.find(n => n.id === client.nationality_id);
        const agency = this.$store.state.available_agencies.find(a => a.id === client.agency_id);
        const source = this.$store.state.itemsSources.find(s => s.id === client.source_id);
        const comment = client.comment || '';

        return {
          ...client,
          programProgress: client.programProgress,
          comment,

          firstAccommodationStart: client.firstAccommodationStart,
          firstAccommodationName: client.firstAccommodationName,
          firstAccommodationFamily: client.firstAccommodationFamily,
          firstAccommodationId: client.firstAccommodationId,
          firstAccommodationStatus: client.firstAccommodationStatus,
          firstAccommodationContactId: client.firstAccommodationContactId,

          nationality: nationality
            ? nationality.nationality
            : client.nationality,

          source: agency
            ? agency.name
            : (source ? source.name : client.source)
        };
      });

      const commentsResponse = await this.$store.dispatch(
        'getWeeklyComments',
        this.weekRange.start.format('YYYY-MM-DD')
      );

      const commentsMap = {};

      commentsResponse.data.forEach(comment => {
        commentsMap[comment.client_id] = comment.comment;
      });

      this.clientsTable.forEach(client => {
        client.comment = commentsMap[client.id] || '';
      });

      this.$store.dispatch('setLoadingApp', 0);

    },

    async getPickups() {
      this.$store.dispatch('setLoadingApp', 1);

      try {

        const results = await this.$store.dispatch('getPickupDashboard', {
          start: this.weekRange.start.format('YYYY-MM-DD'),
          end: this.weekRange.end.format('YYYY-MM-DD')
        });

        this.pickupClients = results.map(r => ({
          ...r,
          resourceId: r.resource_id
        }));

        this.pickupClients = results
          .map(item => {

            const agency = this.$store.state.available_agencies.find(
              a => a.id === item.agency_id
            );

            const source = this.$store.state.itemsSources.find(
              s => s.id === item.source_id
            );

            return {
              id: item.id,
              full_name: item.full_name,

              resourceId: item.resourceId || item.resource_id,
              resourceName: item.resourceName,
              start: item.start,

              source: agency
                ? agency.name
                : (source ? source.name : item.source),

              pickup_date: item.pickup_date,
              pickup_time: item.pickup_time,
              pickup_type: item.pickup_type,
              pickup_company: item.pickup_company,
              pickup_nr: item.pickup_nr,
              pickup_from: item.pickup_from,
              pickup_address: item.pickup_address,

              pickupStaff: item.pickup_staff_id,
              pickupWarning:
                item.pickup_date && item.start
                  ? dayjs(item.pickup_date).format('YYYY-MM-DD') !== dayjs(item.start).format('YYYY-MM-DD')
                  : false,

              firstAccommodationName: item.resourceName,
              firstAccommodationFamily: item.family,
              firstAccommodationContactId: item.contact_id,

              accommodationStatus: item.statusResourceId,

              arrived: item.pickup_date
                ? dayjs(item.pickup_date).format('MM-DD')
                : '',

              end: item.end
              ? dayjs(item.end).format('MM-DD')
              : ''
            };
          })
          .sort((a, b) => {

            const dateA = dayjs(a.pickup_date);
            const dateB = dayjs(b.pickup_date);

            if (dateA.isSame(dateB)) {
              return (a.pickup_time || '')
                .localeCompare(b.pickup_time || '');
            }

            return dateA.valueOf() - dateB.valueOf();
          });

      } finally {
        this.$store.dispatch('setLoadingApp', 0);
      }
    },

    toggleType(type) {
      let updated = [...this.activeTypes];

      if (updated.includes(type)) {
        updated = updated.filter(t => t !== type);
      } else {
        updated.push(type);
      }

      this.activeTypes = updated; // triggers setter → store
    },

    goTeaching(resourceId) {
      this.$router.push({ name: 'Teaching', params: { resourceId: resourceId } })
    },

    goTour(resourceId) {
      this.$router.push({ name: 'TourAgencies', params: { resourceId: resourceId } })
    },

    goProject(resourceId) {
      this.$router.push({ name: 'ProjectPlacements', params: { resourceId: resourceId } })
    },

    goAccommodation(resourceId) {
      this.$router.push({ name: 'Accommodation', params: { resourceId: resourceId } })
    },

    prevWeek() {
      this.currentWeekOffset--;
      this.getClients();
      this.getPickups();
      this.getProjectDropoffs();
    },

    nextWeek() {
      this.currentWeekOffset++;
      this.getClients();
      this.getPickups();
      this.getProjectDropoffs();
    },

    goToday() {
      this.currentWeekOffset = 0;
      this.getClients();
      this.getPickups();
      this.getProjectDropoffs();
    },

    getStatusDotColor(statusId) {
      if (statusId === 3 || statusId === 4) return 'green';
      if (statusId === 2) return 'orange';
      return 'red';
    },

    isActiveToday(start, end) {
      if (!start || !end) return false;

      const today = dayjs();
      return today.isSameOrAfter(dayjs(start), 'day') &&
            today.isSameOrBefore(dayjs(end), 'day');
    },

    getStatusName(statusId) {
      const status = this.$store.state.statusResources.find(s => s.id === statusId);
      return status ? status.name : 'Unknown';
    },

    getUserName(id) {
      const user = this.$store.state.users.find(u => u.id === id);
      return user ? user.name : '';
    },

    async savePickupStaff(item) {
      console.log('savePickupStaff', item);
      await this.$store.dispatch('updatePickupStaff', {
        resourceId: item.resourceId,
        staffId: item.pickupStaff
      });
    },

    async clearPickupStaff(item) {
      await this.$store.dispatch('updatePickupStaff', {
        resourceId: item.resourceId,
        staffId: null,
      });
    },

    printPickupList() {
      const printContents = document.getElementById('printPickup').innerHTML;
      const win = window.open('', '', 'width=900,height=700');

      win.document.write(`
        <html>
          <head>
            <title>Pickup List</title>
            <style>
              body { font-family: Arial; padding: 10px; font-size: 12px; }
              h2 { margin-bottom: 10px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ccc; padding: 4px; }
              th { background: #f5f5f5; text-align: left; font-size: 12px; }
              td { font-size: 11px; line-height: 1.2; }
            </style>
          </head>
          <body>
            ${printContents}
          </body>
        </html>
      `);

      win.document.close();

      // ✅ wait until content is loaded
      win.onload = () => {
        win.focus();

        win.print();

        // ✅ close AFTER print (works better than onafterprint alone)
        setTimeout(() => {
          win.close();
        }, 10);
      };
    },

    getContactInfo(contactId) {
      if (!this.contactsById) return null;
      const contact = this.contactsById.get(contactId);
      if (!contact) return null;

      const fullName = `${contact.name || ''} ${contact.surname || ''}`.trim();

      return {
        name: fullName,
        phone: contact.phone
      };
    },

    getPickupIcon(type) {
      if (!type) return '✈️';

      const t = type.toLowerCase();

      if (t.includes('flight') || t.includes('airport')) return '✈️';
      if (t.includes('bus')) return '🚐';
      if (t.includes('train')) return '🚆';
      if (t.includes('car') || t.includes('taxi')) return '🚗';
      if (t.includes('address') || t.includes('hotel') || t.includes('hostal')) return '🏨';

      return '✈️'; // default
    },

    async getProjectDropoffs() {
      const results = await this.$store.dispatch('getProjectDropoffs', {
        weekStart: this.weekRange.start.format('YYYY-MM-DD'),
        weekEnd: this.weekRange.end.format('YYYY-MM-DD')
      });

      this.projectDropoffs = results || [];
    },

    getDropoffStatusColor(statusId) {
      const status = this.getResourceStatus(statusId);
      return status?.color || 'grey';
    },

    getResourceStatus(statusId) {
      return this.$store.state.statusResources.find(s => s.id === statusId);
    },

    async saveDropoffTime(item) {
      try {
        await this.$store.dispatch('updateDropoffStaff', {
          resourceId: item.resourceId,
          dropoffTime: item.dropoffTime
        });
      } catch (err) {
        console.error(err);
      }
    },

    async saveDropoffFrom(item) {
      try {
        await this.$store.dispatch('updateDropoffStaff', {
          resourceId: item.resourceId,
          dropoffFrom: item.dropoffFrom
        });
      } catch (err) {
        console.error(err);
      }
    },

    async saveDropoffStaff(item) {

      try {
        await this.$store.dispatch('updateDropoffStaff', {
          resourceId: item.resourceId,
          dropoffPerson:
            typeof item.dropoffPerson === 'string'
              ? item.dropoffPerson
              : item.dropoffPerson?.name || null
        });

        this.$store.dispatch('successMessage', 'Drop-off staff member saved');

      } catch (err) {
        console.error(err);
      }
    },

    async saveDropoffDetails(item) {
      try {
        await this.$store.dispatch('updateDropoffDetails', {
          resourceId: item.resourceId,
          dropoff_time: item.dropoff_time,
          dropoff_from: item.dropoff_from,
          dropoff_comment: item.dropoff_comment
        });

        this.$store.dispatch('successMessage', 'Drop-off details saved');

      } catch (err) {
        console.error(err);
      }
    },

    async saveDropoffComment(item) {
      try {
        await this.$store.dispatch('updateDropoffStaff', {
          resourceId: item.resourceId,
          dropoffComment: item.dropoffComment
        });
      } catch (err) {
        console.error(err);
      }
    },

    printDropoffList() {
      const printContents = document.getElementById('printDropoff').innerHTML;
      const win = window.open('', '', 'width=1000,height=800');

      win.document.write(`
        <html>
          <head>
            <title>Drop-off List</title>
            <style>
              body { font-family: Arial; padding: 10px; font-size: 12px; }
              h2 { margin-bottom: 10px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ccc; padding: 4px; }
              th { background: #f5f5f5; text-align: left; font-size: 12px; }
              td { font-size: 11px; line-height: 1.2; }
            </style>
          </head>
          <body>
            ${printContents}
          </body>
        </html>
      `);

      win.document.close();

      // ✅ wait until content is loaded
      win.onload = () => {
        win.focus();

        win.print();

        // ✅ close AFTER print (works better than onafterprint alone)
        setTimeout(() => {
          win.close();
        }, 10);
      };
    },

    async saveWeeklyComment(item) {
      try {
        await this.$store.dispatch('saveWeeklyComment', {
          week_start: this.weekRange.start.format('YYYY-MM-DD'),
          client_id: item.id,
          comment: item.comment || ''
        });
      } catch (err) {
        console.error(err);
      }
    },

    async showCommentHistory(item) {
      const results = await this.$store.dispatch(
        'showCommentHistory',
        item.id
      );

      this.commentHistory = results.data;
      this.commentHistoryClient = item.full_name;
      this.commentHistoryDialog = true;
    },

    async copyCommentHistory() {
      const text = this.commentHistory
        .map(entry => {
          return `${this.formattedDate(entry.week_start, 'monthAndDay')}
    ${entry.comment}`;
        })
        .join('\n\n');

      await navigator.clipboard.writeText(text);

      this.$store.dispatch('successMessage', 'Comment history copied to clipboard');
    },

  },
}
</script>

<style scoped>
.range-block {
  padding: 6px 8px;
  border-radius: 6px;
  margin-bottom: 6px;
}

/* base colors per type */
.range-project {
  background-color: #e3f2fd; /* blue */
}

.range-accommodation {
  background-color: #fff3e0; /* orange */
}

.range-spanish {
  background-color: #e8f5e9; /* green */
}

.range-tour {
  background-color: #f3e5f5; /* purple */
}

/* overlap override */
.range-overlap {
  background-color: #ffcdd2 !important; /* stronger red */
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  opacity: 0.7;
  transition: 0.2s;
}

.legend-active {
  opacity: 1;
  font-weight: 600;
}

.legend-box {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  margin-right: 8px;
  display: inline-block;
}

.range-block div:hover {
  text-decoration: underline;
}

.active-today-border {
  border-radius: 4px;
  padding: 2px 4px;
}

.range-project.active-today-border {
  border: 2px solid rgba(25, 118, 210, 0.5);
}

.range-accommodation.active-today-border {
  border: 2px solid rgba(239, 108, 0, 0.5);
}

.range-spanish.active-today-border {
  border: 2px solid rgba(46, 125, 50, 0.5);
}

.range-tour.active-today-border {
  border: 2px solid rgba(106, 27, 154, 0.5);
}

.active-today-example {
  background-color: #eee;
  border: 1px solid rgba(0,0,0,0.4);
}

::v-deep(.v-select .v-select__selection) {
  font-size: 16px !important;
  font-weight: 500;
}

::v-deep(.pickup-item) {
  font-size: 16px;
  font-weight: 500;
  padding: 6px 0;
}

::v-deep(.pickup-menu .v-list-item) {
  min-height: 40px !important;
}

@media print {

  /* hide everything except pickup tab */
  .v-tabs,
  .v-btn,
  .legend-item,
  .v-text-field {
    display: none !important;
  }

  /* make table clean */
  .v-data-table {
    box-shadow: none !important;
  }

  .range-block {
    background: none !important;
    border: 1px solid #ccc !important;
  }
  
  .pickup-group {
    font-size: 13px;
    line-height: 1.3;
  }

  .pickup-header {
    font-weight: 600;
    margin-bottom: 2px;
  }

  .pickup-line {
    font-size: 12px;
  }

  .gap-line {
    font-size: 12px;
    color: red;
    margin: 2px 0 6px 0;
    font-weight: 600;
    display: flex;
    align-items: center;
  }

  .gap-line::before {
    content: "";
    flex: 1;
    height: 1px;
    background: red;
    margin-right: 6px;
  }

  .gap-line::after {
    content: "";
    flex: 1;
    height: 1px;
    background: red;
    margin-left: 6px;
  }

}

</style>